import React, { useState, useEffect } from 'react';
import { newsAPI } from '../services/api';
import './News.css';

interface NewsItem {
  id: number;
  title: string;
  link: string;
  image: string | null;
  excerpt: string;
  date: string;
  source: string;
}

const News: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await newsAPI.getAll();
      if (response.data.success) {
        setNews(response.data.data);
      } else {
        setError('Failed to fetch news');
      }
    } catch (err) {
      setError('Error loading news. Please try again later.');
      console.error('Error fetching news:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleExternalLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="news-page">
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading latest news...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="news-page">
        <div className="container">
          <div className="error">
            <h2>Error Loading News</h2>
            <p>{error}</p>
            <button onClick={fetchNews} className="btn btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="news-page">
      <div className="container">
        {/* Header */}
        <div className="news-header">
          <h1>Latest News from Oil Now</h1>
          <p>Stay updated with the latest developments in Guyana's oil and gas sector</p>
        </div>

        {/* News Grid */}
        <div className="news-grid">
          {news.map((item) => (
            <article key={item.id} className="news-card">
              {item.image && (
                <div className="news-image">
                  <img src={item.image} alt={item.title} />
                </div>
              )}
              <div className="news-content">
                <div className="news-meta">
                  <span className="news-date">{formatDate(item.date)}</span>
                  <span className="news-source">{item.source}</span>
                </div>
                <h3 className="news-title">{item.title}</h3>
                <p className="news-excerpt">{item.excerpt}</p>
                <button
                  onClick={() => handleExternalLink(item.link)}
                  className="btn btn-primary"
                >
                  Read Full Article
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* No News Message */}
        {news.length === 0 && !loading && !error && (
          <div className="no-news">
            <h3>No News Available</h3>
            <p>Check back later for the latest updates from Guyana's oil and gas sector.</p>
          </div>
        )}

        {/* Refresh Button */}
        <div className="news-actions">
          <button onClick={fetchNews} className="btn btn-secondary">
            Refresh News
          </button>
        </div>
      </div>
    </div>
  );
};

export default News;
